const toMinutes = (value = "00:00") => {
  const [hour, minute] = value.split(":").map(Number);
  return (hour || 0) * 60 + (minute || 0);
};

const tehranParts = (date, options) =>
  new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Tehran", ...options }).formatToParts(date);

const partValue = (parts, type) => parts.find((item) => item.type === type)?.value;

const tehranDate = (date) => {
  const parts = tehranParts(date, { year: "numeric", month: "2-digit", day: "2-digit" });
  return `${partValue(parts, "year")}-${partValue(parts, "month")}-${partValue(parts, "day")}`;
};

const tehranTime = (date) => {
  const parts = tehranParts(date, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${partValue(parts, "hour")}:${partValue(parts, "minute")}`;
};

const reservationTableId = (reservation) =>
  String(reservation.tableId?._id || reservation.tableId || "");

export const createFloorObservation = ({ now = new Date(), viewAt = "" } = {}) => {
  if (!viewAt) return { date: tehranDate(now), time: tehranTime(now), timestamp: now };
  const [date, time = "00:00"] = viewAt.split("T");
  return { date, time, timestamp: new Date(viewAt) };
};

export const fitFloorPlanToContent = (floorPlan, elements = []) => {
  const fallbackPlan = floorPlan || { width: 1000, height: 650 };
  if (!elements.length) return { plan: fallbackPlan, elements: [] };

  const minX = Math.min(...elements.map((item) => item.x));
  const minY = Math.min(...elements.map((item) => item.y));
  const maxX = Math.max(...elements.map((item) => item.x + item.width));
  const maxY = Math.max(...elements.map((item) => item.y + item.height));
  const padding = 36;
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const width = Math.max(450, contentWidth + padding * 2);
  const height = Math.max(280, contentHeight + padding * 2);
  const offsetX = (width - contentWidth) / 2 - minX;
  const offsetY = (height - contentHeight) / 2 - minY;

  return {
    plan: { ...fallbackPlan, width, height },
    elements: elements.map((item) => ({
      ...item,
      x: item.x + offsetX,
      y: item.y + offsetY,
    })),
  };
};

export const buildFloorPlanSnapshot = ({
  floorPlan,
  elements = [],
  tables = [],
  reservations = [],
  blocks = [],
  observation = createFloorObservation(),
}) => {
  const observationMinutes = toMinutes(observation.time);
  const selectedTime = observation.timestamp.getTime();

  const activeBlocks = blocks.filter((block) => {
    const start = new Date(block.startAt).getTime();
    const end = new Date(block.endAt).getTime();
    return start <= selectedTime && end > selectedTime;
  });

  const relevantReservations = reservations
    .filter(
      (reservation) =>
        (reservation.reservationDate || reservation.date) === observation.date &&
        ["CONFIRMED", "SEATED"].includes(String(reservation.status || "").toUpperCase())
    )
    .filter(
      (reservation) =>
        String(reservation.status || "").toUpperCase() === "SEATED" ||
        toMinutes(reservation.endTime || reservation.startTime) >= observationMinutes
    )
    .sort((first, second) => toMinutes(first.startTime) - toMinutes(second.startTime));

  const reservationByTable = {};
  relevantReservations.forEach((reservation) => {
    const tableId = reservationTableId(reservation);
    const status = String(reservation.status || "").toUpperCase();
    if (tableId && (!reservationByTable[tableId] || status === "SEATED")) {
      reservationByTable[tableId] = reservation;
    }
  });

  const states = {};
  tables.forEach((table) => {
    if (table.isActive === false) states[String(table._id || table.id)] = "INACTIVE";
  });
  Object.entries(reservationByTable).forEach(([tableId, reservation]) => {
    states[tableId] = String(reservation.status).toUpperCase() === "SEATED" ? "SEATED" : "RESERVED";
  });
  activeBlocks.forEach((block) => {
    states[String(block.tableId?._id || block.tableId)] = "BLOCKED";
  });

  const numberFormatter = new Intl.NumberFormat("fa-IR", { useGrouping: false });
  const tableDetails = Object.fromEntries(
    tables.map((table) => {
      const tableId = String(table._id || table.id);
      const reservation = reservationByTable[tableId];
      const capacity = `${numberFormatter.format(table.capacity || 0)} نفر`;
      return [tableId, reservation ? `${capacity} · ${reservation.startTime}` : capacity];
    })
  );

  return {
    activeBlocks,
    relevantReservations,
    reservationByTable,
    states,
    tableDetails,
    displayMap: fitFloorPlanToContent(floorPlan, elements),
  };
};
